import { eq, int, mul, neg, rat, toNumber } from '../../math/rational.ts';
import type { Rng } from '../../math/rng.ts';
import type { GeneratedProblemCore, MathMarkup, ProblemTemplate } from '../../types.ts';
import {
  cappedPow,
  fitsPhone,
  gcd,
  ipow,
  poly,
  problem,
  retry,
  roundSqrt,
  safeRat,
  scaled,
  type Instruction,
} from './helpers-g11-12.ts';

/**
 * Grade 11 templates (CAPS FET, middle year): quadratic equations with a ≠ 1,
 * the discriminant and completing the square, surds, exponential equations and
 * rational exponents, arithmetic sequences and quadratic number patterns.
 *
 * Ids and specs: docs/CONTENT_CATALOGUE.md and levels/levels.ts TEMPLATE_CATALOGUE.
 * Answers are whole numbers except where the maths makes a fraction natural
 * (roots of ax² + bx + c = 0, negative rational exponents).
 */

const SOLVE_X: Instruction = { key: 'solve', params: { v: 'x' } };

// ───────────────────────────── g11.quadratic-a ─────────────────────────────

/** A linear factor (p·x − q); its root is q/p. */
interface RootFactor {
  readonly p: number;
  readonly q: number;
}

/** Ways to split the leading coefficient a = p·r between the factors (p·x − q)(r·x − s), gentlest first. */
const SPLITS_EASY: readonly (readonly [number, number])[] = [[1, 2], [1, 3]];
const SPLITS_MID: readonly (readonly [number, number])[] = [[1, 2], [1, 3], [1, 4], [1, 5]];
const SPLITS_HARD: readonly (readonly [number, number])[] = [[1, 2], [1, 3], [1, 4], [2, 2], [1, 5], [1, 6], [2, 3]];

/** Ask for the larger or the smaller root of a·x² + b·x + c = 0, whose factors are given. */
function rootProblem(rng: Rng, a: number, b: number, c: number, factors: readonly [RootFactor, RootFactor]): GeneratedProblemCore {
  const [f0, f1] = factors;
  const larger = rng.chance(0.5);
  const f0IsAnswer = larger === toNumber(rat(f0.q, f0.p)) > toNumber(rat(f1.q, f1.p));
  const [mine, theirs] = f0IsAnswer ? [f0, f1] : [f1, f0];
  const answer = rat(mine.q, mine.p);
  return problem(
    `${poly([[a, 2], [b, 1], [c, 0]])} = 0`,
    answer,
    [
      rat(theirs.q, theirs.p), // gave the other root (compared the roots the wrong way round)
      neg(answer), // sign slip reading the root off its factor: (2x − 1) ⇒ x = −1/2
      mine.p !== 1 ? int(mine.q) : null, // forgot to divide by x's coefficient: 2x − 1 = 0 ⇒ x = 1
      mul(answer, int(2)), // quadratic formula divided by a instead of 2a
      answer.d !== 1 ? rat(answer.d, answer.n) : null, // flipped the fraction: 2x = 1 ⇒ x = 2
    ],
    { key: larger ? 'largerRoot' : 'smallerRoot' },
    { keepFractions: true },
  );
}

/** a·x² + b·x + c = 0 with a ≥ 2 and rational roots, built from its factors (p·x − q)(r·x − s). */
const quadraticA: ProblemTemplate = {
  id: 'g11.quadratic-a',
  grade: 11,
  title: 'ax² + bx + c = 0 (a ≠ 1): larger or smaller root',
  parTimeMs: 24000,
  generate(rng, d) {
    const splits = d < 0.35 ? SPLITS_EASY : d < 0.7 ? SPLITS_MID : SPLITS_HARD;
    const rootMax = scaled(d, 3, 5); // numerators 1–3 at difficulty 0: 2x² − 7x + 3, not 3x² − 16x + 16
    const negChance = d < 0.35 ? 0 : 0.5 * d; // all roots positive in the gentle band
    const numerator = () => rng.int(1, rootMax) * (rng.chance(negChance) ? -1 : 1);
    return retry(
      100,
      () => {
        const [p, r] = rng.pick(splits);
        const q = numerator();
        const s = numerator();
        // Roots in lowest terms keep the equation primitive (gcd(a, b, c) = 1): a genuine
        // a ≠ 1 equation, never a monic one multiplied through by a constant.
        if (gcd(q, p) !== 1 || gcd(s, r) !== 1) return null;
        if (eq(rat(q, p), rat(s, r))) return null; // a double root has no "larger" one
        const a = p * r;
        const b = -(p * s + q * r);
        const c = q * s;
        if (Math.abs(b) > 20 || Math.abs(c) > 25) return null;
        return rootProblem(rng, a, b, c, [{ p, q }, { p: r, q: s }]);
      },
      () => rootProblem(rng, 2, -7, 3, [{ p: 1, q: 3 }, { p: 2, q: 1 }]),
    );
  },
};

// ───────────────────────────── g11.surd-simplify ─────────────────────────────

const SQUARE_FREE_EASY = [2, 3, 5] as const;
const SQUARE_FREE_MID = [2, 3, 5, 6, 7] as const;
const SQUARE_FREE_ALL = [2, 3, 5, 6, 7, 10, 11, 13] as const;

/** √(k²m) = □√m, with a coefficient in front (c√(k²m) = □√m) in the upper half. */
const surdSimplify: ProblemTemplate = {
  id: 'g11.surd-simplify',
  grade: 11,
  title: 'Simplify a surd: √(k²m) = □√m',
  parTimeMs: 14000,
  allowNegative: false,
  generate(rng, d) {
    const pool = d < 0.35 ? SQUARE_FREE_EASY : d < 0.7 ? SQUARE_FREE_MID : SQUARE_FREE_ALL;
    const kMax = scaled(d, 3, 10); // √8 … √45 at difficulty 0
    const radicandMax = scaled(d, 50, 500);
    return retry(
      100,
      () => {
        const m = rng.pick(pool);
        const k = rng.int(2, kMax);
        const radicand = k * k * m;
        if (radicand > radicandMax) return null;
        const coef = d >= 0.5 && rng.chance(0.4) ? rng.int(2, 5) : 1;
        if (coef === 1) {
          return problem(`\\sqrt{${radicand}} = \\box\\sqrt{${m}}`, int(k), [
            int(k * k), // took the square factor out without rooting it: √50 = 25√2
            int(roundSqrt(radicand)), // estimated √n instead of simplifying: √50 ≈ 7
            int(k * m), // took both factors out from under the root: √(25·2) → 5·2
          ]);
        }
        return problem(`${coef}\\sqrt{${radicand}} = \\box\\sqrt{${m}}`, int(coef * k), [
          int(k), // simplified the surd but dropped the coefficient in front
          int(coef + k), // added the outside numbers instead of multiplying
          int(coef * k * k), // took the square factor out without rooting it
        ]);
      },
      () => problem('\\sqrt{50} = \\box\\sqrt{2}', int(5), [int(25), int(7), int(10)]),
    );
  },
};

// ───────────────────────────── g11.exp-eq-bases ─────────────────────────────

/** A prime and its largest power used as a base (2 → 32, 3 → 81, 5 → 125). */
interface PrimeFamily {
  readonly prime: number;
  readonly maxPow: number;
}

const FAMILIES_EASY: readonly PrimeFamily[] = [
  { prime: 2, maxPow: 3 },
  { prime: 3, maxPow: 3 },
];
const FAMILIES: readonly PrimeFamily[] = [
  { prime: 2, maxPow: 5 },
  { prime: 3, maxPow: 4 },
  { prime: 5, maxPow: 3 },
];

/** prime^pow written as a base: `8`, or `(\frac{1}{8})` for a negative power. */
function powerBase(prime: number, pow: number): MathMarkup {
  return pow > 0 ? `${ipow(prime, pow)}` : `(\\frac{1}{${ipow(prime, -pow)}})`;
}

/**
 * (p^u)^(x + k1) = (p^v)^(x + k2), e.g. 4^x = 8^(x − 1): rewrite both sides as
 * powers of p and equate exponents, u(x + k1) = v(x + k2). Built backwards
 * from a whole-number answer; reciprocal bases (1/8)^… appear at the top.
 */
const expEqBases: ProblemTemplate = {
  id: 'g11.exp-eq-bases',
  grade: 11,
  title: 'Exponential equation: rewrite to a common base',
  parTimeMs: 22000,
  generate(rng, d) {
    const easy = d < 0.35;
    return retry(
      150,
      () => {
        const family = rng.pick(easy ? FAMILIES_EASY : FAMILIES);
        const x = easy ? rng.int(1, 4) : rng.intExcept(d >= 0.7 ? -3 : 1, scaled(d, 4, 6), [0]);
        const u = easy ? rng.int(2, family.maxPow) : rng.int(1, family.maxPow);
        let v = easy ? 1 : rng.intExcept(1, family.maxPow, [u]);
        if (d >= 0.7 && rng.chance(0.35)) v = -v;
        const k1 = easy ? rng.int(-1, 1) : rng.int(-3, 3);
        const rest = u * (x + k1) - v * x; // = v·k2
        if (rest % v !== 0) return null;
        const k2 = rest / v + 0; // `+ 0` normalizes -0
        if (Math.abs(k2) > 9) return null;
        // Keep the answer from being readable straight off the prompt: 4^x = 2^(x + 3) → 3,
        // or equal exponents 32^(x + 1) = 8^(x + 1) → −1.
        if ((k1 === 0 && k2 === x) || (k2 === 0 && k1 === x) || k1 === k2) return null;
        const left = `${powerBase(family.prime, u)}^{${poly([[1, 1], [k1, 0]])}}`;
        const right = `${powerBase(family.prime, v)}^{${poly([[1, 1], [k2, 0]])}}`;
        const prompt = rng.chance(0.5) ? `${left} = ${right}` : `${right} = ${left}`;
        if (!fitsPhone(prompt)) return null;
        return problem(
          prompt,
          int(x),
          [
            safeRat(k2 - k1, u - v), // didn't multiply the constant by the power: ux + k1 = vx + k2
            safeRat(u * k2 - v * k1, v - u), // swapped the powers of the two bases
            int(-x), // sign slip moving terms across the equals sign
            safeRat(v * k2 - (u + 1) * k1, u + 1 - v), // misread a base's power (e.g. 8 = 2⁴)
          ],
          SOLVE_X,
        );
      },
      () => problem('4^{x} = 8^{x - 1}', int(3), [int(1), int(-3), int(-2)], SOLVE_X),
    );
  },
};

// ───────────────────────────── g11.discriminant ─────────────────────────────

/** Δ = b² − 4ac for a·x² + b·x + c = 0 (a, b, c all non-zero). */
function discriminantProblem(a: number, b: number, c: number): GeneratedProblemCore {
  return problem(
    `${poly([[a, 2], [b, 1], [c, 0]])} = 0`,
    int(b * b - 4 * a * c),
    [
      int(b * b + 4 * a * c), // sign slip on the −4ac term
      int(b - 4 * a * c), // forgot to square b
      int(b * b - 2 * a * c), // used 2a (the quadratic formula's denominator) instead of 4a
      b < 0 ? int(-b * b - 4 * a * c) : null, // squared a negative b to a negative: (−5)² = −25
    ],
    { key: 'discriminant' },
  );
}

/** Δ = b² − 4ac of a quadratic; monic with positive b, c and real roots in the gentle band, any signs at the top. */
const discriminant: ProblemTemplate = {
  id: 'g11.discriminant',
  grade: 11,
  title: 'Discriminant Δ = b² − 4ac',
  parTimeMs: 16000,
  generate(rng, d) {
    if (d < 0.35) {
      // Every (b, c) with Δ ≥ 0 equally likely — drawing c from 1..b²/4 would make x² + 2x + 1 a fifth of all problems.
      return retry(
        100,
        () => {
          const b = rng.int(2, 6);
          const c = rng.int(1, 9);
          return b * b >= 4 * c ? discriminantProblem(1, b, c) : null;
        },
        () => discriminantProblem(1, 4, 1),
      );
    }
    const hi = scaled(d, 5, 9);
    const a = d < 0.7 ? rng.int(1, 3) : rng.intExcept(-2, 5, [0]);
    return discriminantProblem(a, rng.intExcept(-hi, hi, [0]), rng.intExcept(-hi, hi, [0]));
  },
};

// ───────────────────────────── g11.arith-term ─────────────────────────────

/** Tₙ = T₁ + (n − 1)d from T₁ and d. */
const arithTerm: ProblemTemplate = {
  id: 'g11.arith-term',
  grade: 11,
  title: 'Arithmetic sequence: find Tₙ from T₁ and d',
  parTimeMs: 14000,
  generate(rng, d) {
    const easy = d < 0.35;
    const first = easy ? rng.int(1, 10) : rng.intExcept(-10, scaled(d, 12, 30), [0]);
    const diff = easy ? rng.int(2, 5) : rng.int(2, scaled(d, 6, 9)) * (rng.chance(0.4) ? -1 : 1);
    const n = easy ? rng.int(6, 10) : rng.int(8, scaled(d, 15, 50));
    return problem(
      `T_1 = ${first} \\sep d = ${diff}`,
      int(first + (n - 1) * diff),
      [
        int(first + n * diff), // used n instead of n − 1
        int(first + (n - 2) * diff), // off by one the other way: counted n − 2 steps
        int(n * diff), // dropped the first term: Tₙ = nd
        int((first + n - 1) * diff), // ignored the order of operations: (T₁ + n − 1) × d
        diff < 0 ? int(first - (n - 1) * diff) : null, // dropped the minus sign of d
      ],
      { key: 'term', params: { n } },
    );
  },
};

// ───────────────────────────── g11.quad-sequence ─────────────────────────────

/** Four terms of a quadratic sequence (constant second difference); give the fifth. */
const quadSequence: ProblemTemplate = {
  id: 'g11.quad-sequence',
  grade: 11,
  title: 'Quadratic sequence: next term',
  parTimeMs: 18000,
  generate(rng, d) {
    return retry(
      100,
      () => {
        const second = d < 0.35 ? 2 : d < 0.7 ? rng.int(1, 4) : rng.intExcept(-4, 6, [0]);
        const firstDiff = d < 0.35 ? rng.int(1, 5) : d < 0.7 ? rng.int(-3, 8) : rng.int(-8, 10);
        const start = d < 0.35 ? rng.int(1, 6) : d < 0.7 ? rng.int(-5, 10) : rng.int(-10, 20);
        const terms = [start];
        for (let i = 0; i < 4; i++) terms.push(terms[i]! + firstDiff + i * second);
        if (terms.some((t) => Math.abs(t) > 99)) return null;
        const [, , , fourth, fifth] = terms as [number, number, number, number, number];
        const lastDiff = firstDiff + 2 * second; // T₄ − T₃
        const prompt = `${terms.slice(0, 4).join(', ')}, \\box`;
        if (!fitsPhone(prompt)) return null;
        return problem(
          prompt,
          int(fifth),
          [
            int(fourth + lastDiff), // treated it as arithmetic: reused the last first difference
            int(fourth + lastDiff + 2 * second), // added the second difference twice
            int(fourth + lastDiff - second), // subtracted the second difference
          ],
          { key: 'next' },
        );
      },
      () => problem('3, 8, 15, 24, \\box', int(35), [int(33), int(37), int(31)], { key: 'next' }),
    );
  },
};

// ───────────────────────────── g11.complete-square ─────────────────────────────

/** x² + bx + c = (x + b/2)² − □ with b even and c < (b/2)², so □ = (b/2)² − c > 0. */
const completeSquare: ProblemTemplate = {
  id: 'g11.complete-square',
  grade: 11,
  title: 'Complete the square: x² + bx + c = (x + b/2)² − □',
  parTimeMs: 18000,
  allowNegative: false,
  generate(rng, d) {
    return retry(
      100,
      () => {
        const half =
          d < 0.35 ? rng.int(2, 4) : rng.int(d < 0.7 ? 1 : 2, scaled(d, 5, 7)) * (rng.chance(0.5) ? -1 : 1);
        const square = half * half;
        const c = d < 0.35 ? rng.int(1, square - 1) : rng.intExcept(d < 0.7 ? -9 : -15, square - 1, [0]);
        const b = 2 * half;
        const prompt = `${poly([[1, 2], [b, 1], [c, 0]])} = (${poly([[1, 1], [half, 0]])})^2 - \\box`;
        if (!fitsPhone(prompt)) return null;
        return problem(
          prompt,
          int(square - c),
          [
            int(square + c), // sign slip: added c instead of subtracting it
            int(b * b - c), // squared b instead of half of b
            int(Math.abs(b) - c), // forgot to square: used b itself
            int(Math.abs(half) - c), // halved b but forgot to square it
            int(square), // gave the square (b/2)² and forgot the constant c
          ],
          undefined,
          { positiveOnly: true },
        );
      },
      () => problem('x^2 + 6x + 2 = (x + 3)^2 - \\box', int(7), [int(11), int(34), int(4)]),
    );
  },
};

// ───────────────────────────── g11.exp-rational ─────────────────────────────

/** Exponents [p, q] meaning p/q (lowest terms), gentlest first. */
const EXPONENTS_EASY: readonly (readonly [number, number])[] = [[1, 2], [1, 3]];
const EXPONENTS_MID: readonly (readonly [number, number])[] = [
  [3, 2], [2, 3], [4, 3], [5, 3], [3, 4], [1, 4], [2, 5], [1, 5],
];
const EXPONENTS_HARD: readonly (readonly [number, number])[] = [...EXPONENTS_MID, [5, 2], [5, 4], [3, 5], [1, 2], [1, 3]];

/** Largest base and value used by g11.exp-rational (e.g. 256^(3/4) = 64, 9^(5/2) = 243). */
const EXP_RATIONAL_MAX = 256;
/** Powers of the base used as misconceptions stay three-digit, like the numbers in the prompt (8² = 64, not 81² = 6561). */
const EXP_RATIONAL_MISCONCEPTION_MAX = 999;

/** a^(p/q) with a a perfect q-th power; a negative exponent (→ a unit fraction) at the top. */
const expRational: ProblemTemplate = {
  id: 'g11.exp-rational',
  grade: 11,
  title: 'Rational exponents: a^(p/q)',
  parTimeMs: 12000,
  generate(rng, d) {
    const pool = d < 0.25 ? EXPONENTS_EASY : d < 0.6 ? EXPONENTS_MID : EXPONENTS_HARD;
    const baseMax = scaled(d, 100, EXP_RATIONAL_MAX);
    return retry(
      150,
      () => {
        const [p, q] = rng.pick(pool);
        const root = rng.int(2, 12);
        const base = ipow(root, q);
        const value = ipow(root, p);
        if (base > baseMax || value > EXP_RATIONAL_MAX) return null;
        const negative = d >= 0.6 && rng.chance(0.4);
        const prompt = `${base}^{${negative ? '-' : ''}\\frac{${p}}{${q}}} = \\box`;
        if (negative) {
          const answer = rat(1, value);
          return problem(prompt, answer, [
            int(value), // ignored the minus sign in the exponent
            neg(answer), // thought a negative exponent makes the value negative
            p > 1 ? rat(1, root) : null, // took the root but forgot the power p
            safeRat(-base * p, q), // multiplied the base by the exponent
          ]);
        }
        return problem(prompt, int(value), [
          p > 1 ? int(root) : null, // took the root but forgot the power p
          safeRat(base * p, q), // multiplied the base by the exponent: 8 × 2/3
          cappedPow(int(base), p, EXP_RATIONAL_MISCONCEPTION_MAX), // raised to the power p, forgot the root (p = 1: ignored the exponent)
          p === 1 ? cappedPow(int(base), q, EXP_RATIONAL_MISCONCEPTION_MAX) : null, // read the exponent upside down: 4^(1/2) as 4²
        ]);
      },
      () => problem('8^{\\frac{2}{3}} = \\box', int(4), [int(2), rat(16, 3), int(64)]),
    );
  },
};

/** Grade 11 templates — ids and specs: docs/CONTENT_CATALOGUE.md and levels/levels.ts TEMPLATE_CATALOGUE. */
export const GRADE_11: ProblemTemplate[] = [
  quadraticA,
  surdSimplify,
  expEqBases,
  discriminant,
  arithTerm,
  quadSequence,
  completeSquare,
  expRational,
];
